<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/dropdown-menu'
import { ref, computed, onMounted, watch, nextTick, PropType } from 'vue'
import { useLibraryStore } from '@/library/library.store'
import ProgressSpinner from 'primevue/progressspinner'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/authentication/user.store'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import { ActionPermissions } from '@/components/limit_action_copy'
import { getAnalyticsTracker } from "@/tracking/analytics/plugin";
import { CONTENT_TYPES } from '@/util/access_visibility'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { deprecatedPresentLevelsSaveEventData } from '@/library/library_event_names'
import { useFolderContentQuota } from '@/library/composables/useFolderContentQuota'
import { LibraryFolder } from '@/library/library_types'

const analytics = getAnalyticsTracker()

const emit = defineEmits<{
  save: [folder: { uuid: string, name: string }, savedContent: unknown]
  saveerror: [error: unknown]
}>()

const props = defineProps({
  contentType: {
    type: String,
    required: true,
  },
  contentTypeLabel: {
    type: String,
    required: true,
  },
  contentUuid: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value: string) => ['lg', 'md', 'xs'].includes(value)
  },
  fullWidth: {
    type: Boolean,
    default: false
  },
  contentTitle: {
    type: String,
    default: ''
  },
  enableNotes: {
    type: Boolean,
    default: false
  },
  notesPlaceholder: {
    type: String,
    default: ''
  },
  actionPermissions: {
    type: Object as PropType<ActionPermissions | null>,
    default: null
  },
  contentPayload: {
    type: Object as PropType<Record<string, unknown>>,
    default: undefined
  }
})

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { currentFolder } = storeToRefs(libraryStore)
const { userId } = storeToRefs(userStore)
const { folderQuotaErrorMessage } = useFolderContentQuota()

const recentFolders = computed(()=> {
  if (props.contentType === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY) {
    return libraryStore.recentUserOwnedFolders
  }
  return libraryStore.recentFolders
})

const isBlocked = computed(() => Boolean(props.actionPermissions?.block))
const showLimitModal = ref(false)
const showSaveToFolderModal = ref(false)
const modalPreselectedFolder = ref<LibraryFolder>()
const isSaving = ref(false)
const analyticsContentTitle = computed(() => props.contentTitle || props.contentTypeLabel)

async function refreshFolders() {
  if (userId.value && !isBlocked.value) {
    await libraryStore.fetchRecentFolders(userId.value)
    await libraryStore.fetchFolders(userId.value)
  }
}

onMounted(refreshFolders)

watch([userId, isBlocked], refreshFolders)

const hasRecentFolders = computed(() => recentFolders.value.length > 0)

function sendAnalyticsEvent(targetFolderUuid?: string) {
  // Use the passed folder UUID or fall back to currentFolder for modal saves
  const folderUuid = targetFolderUuid || currentFolder.value?.uuid
  const userFolderRole = folderUuid ? libraryStore.getUserRoleForFolder(folderUuid) : null
  switch (props.contentType) {
    case CONTENT_TYPES.GOAL:
    case CONTENT_TYPES.SUPER_PAGE: {
      const contentProperties = { cu: props.contentUuid, ct: analyticsContentTitle.value }
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.GOAL, {
        ...contentProperties,
        user_folder_role: userFolderRole,
      }, contentProperties)
      break
    }
    case CONTENT_TYPES.STRATEGY:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.STRATEGY, {
        cu: props.contentUuid,
        ct: analyticsContentTitle.value,
        user_folder_role: userFolderRole,
      })
      break
    case CONTENT_TYPES.PRESENTLEVEL:
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.PRESENT_LEVELS, {
        plw_uuid: props.contentUuid,
        plw_new_student: false,
        user_folder_role: userFolderRole,
        ...deprecatedPresentLevelsSaveEventData(props.contentType, props.contentUuid)
      })
      break
    case CONTENT_TYPES.RESOURCE:
      analytics.sendEvent(EVENT_NAMES.RESOURCES.SAVED, {
        cu: props.contentUuid,
        ct: analyticsContentTitle.value,
        student_uuid: folderUuid,
        user_folder_role: userFolderRole
      })
      break
  }
}

const saveToFirstFolder = async () => {
  if (!hasRecentFolders.value) return
  const firstFolder = recentFolders.value[0]
  if (props.enableNotes) {
    openSaveToFolderModal(firstFolder)
    return
  }
  try {
    const quotaError = folderQuotaErrorMessage(props.contentType, firstFolder.uuid)
    if (quotaError) {
      emit('saveerror', new Error(quotaError))
      return
    }
    isSaving.value = true
    const folder: { uuid: string, name: string } = {
      uuid: firstFolder.uuid,
      name: firstFolder.name
    }

    const savedContent = await libraryStore.saveToLibrary({
      contentType: props.contentType,
      contentUuid: props.contentUuid,
      collectionUuid: firstFolder.uuid,
      contributor: userId.value,
      folder: folder,
      contentPayload: props.contentPayload
    })

    try {
      sendAnalyticsEvent(firstFolder.uuid)
    } catch (ae) {
      console.warn('Analytics event failed to send', ae)
    }
    emit('save', folder, savedContent)
    refreshFolders()
  } catch (error) {
    emit('saveerror', error)
    console.error('Error saving to folder:', error)
  } finally {
    isSaving.value = false
  }
}

function openSaveToFolderModal(preselectedFolder?: LibraryFolder) {
  modalPreselectedFolder.value = preselectedFolder
  showSaveToFolderModal.value = true
}

const menuTrigger = ref<InstanceType<typeof DropdownMenuTrigger> | null>(null)

// The picker is unmounted on close, before PrimeVue could restore focus, and the menu item that
// opened it is gone by then, so focus goes back to the menu's trigger.
function closeSaveToFolderModal() {
  showSaveToFolderModal.value = false
  nextTick(() => (menuTrigger.value?.$el as HTMLElement | undefined)?.focus())
}

function handleModalSave(folder: { uuid: string, name: string }, savedContent: unknown) {
  emit('save', folder, savedContent)
  refreshFolders()
}

const buttonClasses = computed(() => {
  const classes = ['bg-green-300', 'text-white', 'hover:bg-green-400', 'focus-visible:ring-green-400', 'focus:outline-none', 'focus-visible:ring-2', 'focus-visible:ring-offset-2']

  switch (props.size) {
    case 'lg':
      classes.push('text-text-xl', isSaving.value ? 'px-8' : 'px-6', 'py-3', 'rounded-xs')
      break;
    case 'xs':
      classes.push('text-text-sm', isSaving.value ? 'px-8' : 'px-2', 'py-1', 'rounded-sm')
      break;
    default:
      classes.push('text-text-md', isSaving.value ? 'px-8' : 'px-4', 'py-2', 'rounded-md')
  }

  if (props.fullWidth) {
    classes.push('w-full')
  }

  return classes.join(' ')
})

const menuWidthClass = computed(() => props.fullWidth ? 'w-[--reka-dropdown-menu-trigger-width]' : 'w-56')

const iconClasses = computed(() => {
  let iconClasses = ['material-icons-round']
  switch (props.size) {
    case 'xs':
      iconClasses.push('text-lg')
      break;
    default:
      break;
  }
  return iconClasses.join(' ')
})
</script>

<template>
  <div>
    <button v-if="isBlocked" type="button" :class="buttonClasses" @click="showLimitModal = true">
      <span class="flex items-center justify-center gap-1">
        <span class="material-icons-round" :class="iconClasses" aria-hidden="true">save</span>
        <span>Save to Library</span>
      </span>
    </button>
    <!-- Non-modal, so the open menu does not aria-hide the rest of the page, including its own trigger -->
    <DropdownMenu v-else :modal="false">
      <DropdownMenuTrigger ref="menuTrigger" :class="buttonClasses" :aria-label="`Save to Library`">
        <div class="flex items-center justify-center gap-1">
          <slot v-if="!isSaving" name="icon">
            <span class="material-icons-round" :class="iconClasses">save</span>
          </slot>
          <span v-if="isSaving" class="inline-flex items-center">
            <ProgressSpinner class="mr-2 !h-5 !w-5 stroke-white" strokeWidth="4" /><span>Saving...</span>
          </span>
          <slot v-if="!isSaving" name="text">
            <span>Save to Library</span>
          </slot>
          <span v-if="!isSaving" class="material-icons-round" :class="iconClasses">arrow_drop_down</span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent :class="menuWidthClass" align="start">
        <DropdownMenuItem v-if="hasRecentFolders" @click="saveToFirstFolder">
          <span>Save to <span class="fs-exclude">{{ recentFolders[0]?.name }}</span></span>
        </DropdownMenuItem>
        <DropdownMenuItem @click="openSaveToFolderModal()">
          <span>Save to {{ hasRecentFolders ? 'another' : 'a' }} folder</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <PremiumActionModal
    v-if="isBlocked"
    v-model:external-open="showLimitModal"
    :action-permissions="props.actionPermissions"
  />

  <SaveToFolderModal
    v-if="showSaveToFolderModal"
    :content-type="props.contentType"
    :content-type-label="props.contentTypeLabel"
    :content-uuid="props.contentUuid"
    :content-title="props.contentTitle"
    :content-payload="props.contentPayload"
    :preselected-folder="modalPreselectedFolder"
    :enable-notes="props.enableNotes"
    :notes-placeholder="props.notesPlaceholder"
    @update:visible="(val) => { if (!val) closeSaveToFolderModal() }"
    @save="handleModalSave"
    @saveerror="(error) => emit('saveerror', error)"
  />
</template>